import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Briefcase, Users, ClipboardList, ExternalLink, Star, Loader } from 'lucide-react';
import { customersApi, jobsApi } from '../services/api';
import { subscriptionService } from '../services/subscriptionService';
import { useBusinessContext } from '../contexts/BusinessContext';

const EnhancedSuccessPage = () => {
  const navigate = useNavigate();
  const { businessName, user, isFoundingMember } = useBusinessContext();
  const [completionStatus, setCompletionStatus] = useState({
    businessProfile: false,
    firstCustomer: false,
    firstJob: false,
  });
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalJobs: 0,
  });

  useEffect(() => {
    // First refresh subscription data, then check completion status
    refreshSubscriptionData();
  }, []);

  const refreshSubscriptionData = async () => {
    try {
      setSubscriptionLoading(true);
      
      // Wait a moment for webhook to process if coming from checkout
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const status = await subscriptionService.getStatus();
      setSubscriptionInfo(status);
      
      // ✅ UPDATE localStorage with new subscription info
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = {
        ...currentUser,
        subscriptionStatus: status.subscriptionStatus,
        subscriptionPlan: status.subscriptionPlan,
        isTrialActive: status.isTrialActive,
        trialDaysRemaining: status.trialDaysRemaining,
        hasValidAccess: status.hasValidAccess,
        isFoundingMember: status.isFoundingMember === true,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
    } catch (error) {
      console.error('Error refreshing subscription:', error);
    } finally {
      setSubscriptionLoading(false);
      // Now check completion status
      checkCompletionStatus();
    }
  };

  const checkCompletionStatus = async () => {
    try {
      // Check if business profile is set up (business name exists)
      const hasBusinessProfile = !!businessName && businessName.trim() !== '';

      // Check if customers exist
      const customersRes = await customersApi.getAll();
      const hasCustomers = customersRes.data && customersRes.data.length > 0;

      // Check if jobs exist
      const jobsRes = await jobsApi.getAll();
      const hasJobs = jobsRes.data && jobsRes.data.length > 0;

      setCompletionStatus({
        businessProfile: hasBusinessProfile,
        firstCustomer: hasCustomers,
        firstJob: hasJobs,
      });

      // Set stats for displaying to existing users
      setStats({
        totalCustomers: customersRes.data?.length || 0,
        totalJobs: jobsRes.data?.length || 0,
      });
    } catch (error) {
      console.error('Error checking completion status:', error);
      // If there's an error, default to false for all
      setCompletionStatus({
        businessProfile: false,
        firstCustomer: false,
        firstJob: false,
      });
      setStats({
        totalCustomers: 0,
        totalJobs: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Determine if this is a new user or existing user converting from trial
  const isExistingUser = completionStatus.firstCustomer || completionStatus.firstJob;

  // Get plan display info
  // Priority:
  //   1. If founder flag is set, they just subscribed to Pro Founder ($10/mo)
  //   2. Otherwise, use the plan name from subscription data
  //   3. If plan is null (e.g. webhook delayed), default to showing a neutral "Activating..." state
  const getPlanDisplay = () => {
    const planName = subscriptionInfo?.subscriptionPlan;

    // Founders always resolve to Pro Founder — their subscription is pinned to the
    // Pro Founder Stripe price by the backend, regardless of whether the webhook
    // has written SubscriptionPlan yet.
    if (isFoundingMember) {
      return {
        name: 'Pro',
        displayName: 'Pro — Founder',
        color: 'amber',
        icon: '⭐',
        price: '$10/mo',
        standardPrice: '$49/mo',
        isFounder: true,
      };
    }

    switch (planName) {
      case 'Solo':
        return { name: 'Solo', displayName: 'Solo', color: 'blue', icon: '💼', price: '$19/mo', isFounder: false };
      case 'Pro':
        return { name: 'Pro', displayName: 'Professional', color: 'blue', icon: '⭐', price: '$49/mo', isFounder: false };
      case 'Enterprise':
        return { name: 'Enterprise', displayName: 'Enterprise', color: 'purple', icon: '🚀', price: '$129/mo', isFounder: false };
      default:
        // Plan not yet set (webhook may still be processing)
        return {
          name: 'Pending',
          displayName: 'Activating',
          color: 'blue',
          icon: '⏳',
          price: '',
          isFounder: false,
          isPending: true,
        };
    }
  };

  const planDisplay = getPlanDisplay();
  const isEnterprise = planDisplay.name === 'Enterprise';
  const isFounderDisplay = planDisplay.isFounder === true;

  // Feature list resolver based on tier (founders get Pro features)
  const getFeatureTier = () => {
    if (isFoundingMember) return 'Pro';
    return subscriptionInfo?.subscriptionPlan;
  };
  const featureTier = getFeatureTier();

  const onboardingSteps = [
    {
      id: 'businessProfile',
      title: 'Set Up Business Profile',
      description: 'Complete your business information and preferences',
      icon: Briefcase,
      path: '/settings',
      completed: completionStatus.businessProfile,
      helpText: 'Add your business details in the Business Info tab to personalize your experience.',
    },
    {
      id: 'firstCustomer',
      title: 'Add Your First Customer',
      description: 'Start building your customer database',
      icon: Users,
      path: '/customers',
      completed: completionStatus.firstCustomer,
      helpText: 'Create your first customer profile to start tracking jobs and building relationships.',
    },
    {
      id: 'firstJob',
      title: 'Create Your First Job',
      description: 'Begin managing your service projects',
      icon: ClipboardList,
      path: '/jobs',
      completed: completionStatus.firstJob,
      helpText: 'Once you have a customer, create your first job to start managing work and quotes.',
    },
  ];

  const allStepsComplete = Object.values(completionStatus).every(status => status);
  const completedCount = Object.values(completionStatus).filter(Boolean).length;

  const handleGoToDashboard = () => {
    // Force a full page reload to pick up the new user data in context
    window.location.href = '/dashboard';
  };

  // Show loading while subscription data is being fetched
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing your subscription...</h2>
          <p className="text-gray-600">This will only take a moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      {/* Header - matches checkout page */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">S</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">SuddyFlow</h1>
                <p className="text-xs text-gray-500">Veteran-Owned</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Success Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Success Animation & Message */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6 relative">
            <CheckCircle className="w-12 h-12 text-green-600" />
            <div className={`absolute -top-1 -right-1 rounded-full p-1.5 ${isFounderDisplay ? 'bg-amber-400' : 'bg-yellow-400'}`}>
              <Star className={`h-4 w-4 fill-current ${isFounderDisplay ? 'text-amber-900' : 'text-yellow-800'}`} />
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {isFounderDisplay
              ? <>🎉 Welcome, Founding Member!</>
              : <>🎉 Welcome to {planDisplay.displayName}!</>
            }
          </h1>
          
          <p className="text-xl text-gray-600 mb-2">
            Your subscription is now active
          </p>

          {isFounderDisplay && (
            <p className="text-sm text-amber-700 font-semibold mt-2 flex items-center justify-center gap-1">
              🔒 Your Pro rate of $10/month is locked for life
            </p>
          )}
        </div>

        {/* Plan Confirmation Box */}
        <div className={`border-2 rounded-xl p-6 mb-8 bg-gradient-to-r ${
          isFounderDisplay
            ? 'from-amber-50 to-yellow-100 border-amber-300'
            : isEnterprise
              ? 'from-purple-50 to-purple-100 border-purple-200'
              : 'from-blue-50 to-blue-100 border-blue-200'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="text-4xl">{planDisplay.icon}</div>
              <div>
                <h3 className={`text-xl font-bold ${
                  isFounderDisplay ? 'text-amber-900' : isEnterprise ? 'text-purple-900' : 'text-blue-900'
                }`}>
                  {planDisplay.displayName} Plan
                </h3>
                {isFounderDisplay ? (
                  <p className="text-amber-800 flex items-center gap-2">
                    <span className="text-gray-500 line-through text-sm">{planDisplay.standardPrice}</span>
                    <span className="font-bold">{planDisplay.price}</span>
                    <span>• Billed monthly</span>
                  </p>
                ) : planDisplay.isPending ? (
                  <p className="text-blue-700">
                    Finalizing your subscription details…
                  </p>
                ) : (
                  <p className={isEnterprise ? 'text-purple-700' : 'text-blue-700'}>
                    {planDisplay.price} • Billed monthly
                  </p>
                )}
              </div>
            </div>
            <div className={`px-4 py-2 rounded-full font-bold ${
              isFounderDisplay ? 'bg-amber-200 text-amber-900' : 'bg-green-200 text-green-800'
            }`}>
              ✓ Active
            </div>
          </div>
        </div>

        {/* Features Unlocked */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">🔓 Features Unlocked</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {featureTier === 'Solo' && (
              <>
                <FeatureItem>Unlimited Customers</FeatureItem>
                <FeatureItem>Professional Invoicing</FeatureItem>
                <FeatureItem>SuddySnap™ Receipt Scanner</FeatureItem>
                <FeatureItem>Schedule C Tax Reports</FeatureItem>
                <FeatureItem>Expense Tracking</FeatureItem>
                <FeatureItem>Job Management</FeatureItem>
              </>
            )}
            {featureTier === 'Pro' && (
              <>
                <FeatureItem>Everything in Solo +</FeatureItem>
                <FeatureItem>Up to 15 Team Members</FeatureItem>
                <FeatureItem>Route Optimization</FeatureItem>
                <FeatureItem>Automated Email Reminders</FeatureItem>
                <FeatureItem>P&L Financial Reports</FeatureItem>
                <FeatureItem>Customer Portal</FeatureItem>
                <FeatureItem>Priority Support</FeatureItem>
              </>
            )}
            {featureTier === 'Enterprise' && (
              <>
                <FeatureItem>Everything in Pro +</FeatureItem>
                <FeatureItem>Unlimited Team Members</FeatureItem>
                <FeatureItem>API Access</FeatureItem>
                <FeatureItem>Advanced Analytics</FeatureItem>
                <FeatureItem>Multi-Location Support</FeatureItem>
                <FeatureItem>Custom Branding</FeatureItem>
                <FeatureItem>Custom Email Templates</FeatureItem>
                <FeatureItem>Priority Phone Support</FeatureItem>
              </>
            )}
            {!featureTier && (
              <p className="text-sm text-gray-500 italic col-span-2">
                Your features will appear here once your subscription finalizes (usually within a minute).
              </p>
            )}
          </div>
        </div>

        {/* Progress Bar - Only show for new users who need setup */}
        {!isExistingUser && !allStepsComplete && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Setup Progress
              </span>
              <span className="text-sm font-medium text-blue-600">
                {completedCount} of 3 complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(completedCount / 3) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Onboarding Steps - Only show for new users */}
        {!isExistingUser && (
          <>
            <div className="space-y-4 mb-8">
              {onboardingSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.id}
                    className={`bg-white rounded-xl shadow-sm border-2 transition-all duration-200 ${
                      step.completed 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    <button
                      onClick={() => navigate(step.path)}
                      className="w-full p-6 text-left"
                    >
                      <div className="flex items-start gap-4">
                        {/* Step Number / Checkmark */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                          step.completed
                            ? 'bg-green-500 text-white'
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {step.completed ? (
                            <CheckCircle className="w-6 h-6" />
                          ) : (
                            index + 1
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className={`w-5 h-5 ${step.completed ? 'text-green-600' : 'text-blue-600'}`} />
                            <h3 className={`text-lg font-semibold ${
                              step.completed ? 'text-green-900' : 'text-gray-900'
                            }`}>
                              {step.title}
                            </h3>
                          </div>
                          
                          <p className={`text-sm mb-2 ${
                            step.completed ? 'text-green-700' : 'text-gray-600'
                          }`}>
                            {step.description}
                          </p>

                          {/* Help Text for incomplete steps */}
                          {!step.completed && (
                            <p className="text-sm text-blue-600 font-medium">
                              💡 {step.helpText}
                            </p>
                          )}

                          {/* Completed Badge */}
                          {step.completed && (
                            <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mt-2">
                              <CheckCircle className="w-4 h-4" />
                              Completed
                            </div>
                          )}
                        </div>

                        {/* Arrow */}
                        <ArrowRight className={`w-6 h-6 flex-shrink-0 ${
                          step.completed ? 'text-green-600' : 'text-gray-400'
                        }`} />
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* All Steps Complete Message */}
            {allStepsComplete && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      🎊 You're All Set!
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Great job! Your SuddyFlow account is fully configured and ready to go. 
                      Start managing your business like a pro!
                    </p>
                    <button
                      onClick={handleGoToDashboard}
                      className={`inline-flex items-center gap-2 px-6 py-3 ${isEnterprise ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-semibold rounded-lg transition-colors`}
                    >
                      Go to Dashboard
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Existing User Content - Show different message and CTA */}
        {isExistingUser && (
          <div className={`bg-gradient-to-r ${isEnterprise ? 'from-purple-50 to-green-50 border-purple-200' : 'from-blue-50 to-green-50 border-blue-200'} border-2 rounded-xl p-8 mb-8`}>
            <div className="text-center">
              <div className={`flex-shrink-0 w-16 h-16 ${isEnterprise ? 'bg-purple-600' : 'bg-blue-600'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <CheckCircle className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                You're Ready to Go! 🚀
              </h3>
              <p className="text-gray-700 mb-6 max-w-xl mx-auto">
                Welcome back! Your subscription is now active and you have full access to all {planDisplay.displayName} features. 
                Continue managing your {stats.totalCustomers > 0 && `${stats.totalCustomers} customer${stats.totalCustomers !== 1 ? 's' : ''} and `}
                business with confidence.
              </p>
              <button
                onClick={handleGoToDashboard}
                className={`inline-flex items-center gap-2 px-8 py-3 ${isEnterprise ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl`}
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons - Only show for new users with incomplete setup */}
        {!isExistingUser && !allStepsComplete && (
          <>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  // Find first incomplete step or go to dashboard if all complete
                  const nextStep = onboardingSteps.find(step => !step.completed);
                  if (nextStep) {
                    navigate(nextStep.path);
                  } else {
                    handleGoToDashboard();
                  }
                }}
                className={`px-8 py-3 ${isEnterprise ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl flex items-center justify-center gap-2`}
              >
                Start First Step
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <button
                onClick={handleGoToDashboard}
                className="px-8 py-3 bg-white text-gray-700 font-semibold rounded-lg border-2 border-gray-300 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
              >
                I'll Do This Later
              </button>
            </div>

            {/* Helpful Note */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-left max-w-2xl">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold mt-0.5">
                  💡
                </div>
                <div>
                  <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">Don't worry!</strong> You can complete these steps anytime. 
                    A setup checklist will appear in your dashboard to help you track your progress.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Help Section */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-600 mb-2">
            Need help getting started?
          </p>
          <a
            href="mailto:support@suddyflow.com"
            className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
          >
            Contact Support
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            🇺🇸 Veteran-owned business • 🔒 Secure payment by Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

// Feature item component
const FeatureItem = ({ children }) => (
  <div className="flex items-center gap-2 text-gray-700">
    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
    <span>{children}</span>
  </div>
);

export default EnhancedSuccessPage;